import { defined } from "@entrilia/shared/utilities/typeHelper";

export interface MeasuredColour {
  hex: string;
  share: number;
}

interface ColourGroup {
  colour: number;
  count: number;
}

const MIN_OPAQUE_ALPHA = 128;
const MAX_CHANNEL_DIFFERENCE = 16;
const MAX_COLOUR_GROUPS = 64;
const MIN_COLOUR_FRACTION = 0.01;
const MAX_MEASURED_COLOURS = 12;

const channelShifts = [16, 8, 0];

const isNearColour = (left: number, right: number): boolean =>
  channelShifts.every(
    (shift) => Math.abs(((left >> shift) & 0xff) - ((right >> shift) & 0xff)) <= MAX_CHANNEL_DIFFERENCE
  );

const countOpaqueColours = (pixels: ArrayLike<number>): Map<number, number> => {
  const counts = new Map<number, number>();
  for (let i = 0; i + 3 < pixels.length; i += 4) {
    if (defined(pixels[i + 3]) < MIN_OPAQUE_ALPHA) {
      continue;
    }
    const colour = (defined(pixels[i]) << 16) | (defined(pixels[i + 1]) << 8) | defined(pixels[i + 2]);
    counts.set(colour, (counts.get(colour) ?? 0) + 1);
  }
  return counts;
};

const groupColours = (counts: Map<number, number>): ColourGroup[] => {
  const groups: ColourGroup[] = [];
  const byFrequency = [...counts].sort(([, left], [, right]) => right - left);
  for (const [colour, count] of byFrequency) {
    const group = groups.find((candidate) => isNearColour(candidate.colour, colour));
    if (group) {
      group.count += count;
    } else if (groups.length < MAX_COLOUR_GROUPS) {
      groups.push({ colour, count });
    }
  }
  return groups;
};

const toHex = (colour: number): string => colour.toString(16).toUpperCase().padStart(6, "0");

export const summarizeImageColours = (pixels: ArrayLike<number>): MeasuredColour[] => {
  const counts = countOpaqueColours(pixels);
  const opaquePixels = [...counts.values()].reduce((sum, count) => sum + count, 0);
  return groupColours(counts)
    .filter(({ count }) => count / opaquePixels >= MIN_COLOUR_FRACTION)
    .sort((left, right) => right.count - left.count)
    .slice(0, MAX_MEASURED_COLOURS)
    .map(({ colour, count }) => ({ hex: toHex(colour), share: Math.round((count / opaquePixels) * 100) }));
};
